// src/components/StaffDashboard.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import StaffSidebar from "../components/StaffSidebar";
import Modal from "react-modal";
import "../assets/css/staff-dashboard.css";

Modal.setAppElement("#root");

export default function StaffDashboard() {
  const programme = localStorage.getItem("programme");
  const username = localStorage.getItem("username");

  const isProduction = process.env.NODE_ENV === "production";
  const API = isProduction
    ? "https://schools.luth.gov.ng"
    : "http://localhost:3003";
  const PAGE_SIZE = 12;
  const LEVELS = [
    "All",
    "ND1",
    "ND2",
    "HND1",
    "HND2",
    "Postgraduate",
    "Diploma",
  ];
  const CARD_LEVELS = LEVELS.filter((lvl) => lvl !== "All");

  // summary cards
  const [totalStudents, setTotalStudents] = useState(0);
  const [byLevel, setByLevel] = useState([]);

  // students panel
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentLevel, setStudentLevel] = useState("All");
  const [studentPage, setStudentPage] = useState(1);

  // courses panel
  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [courseLevel, setCourseLevel] = useState("All");
  const [coursePage, setCoursePage] = useState(1);

  // profile modal
  const [modalOpen, setModalOpen] = useState(false);
  const [profile, setProfile] = useState(null);

  const handleViewProfile = async (id) => {
    try {
      const { data } = await axios.get(`${API}/api/student/${id}`);
      setProfile(data);
      setModalOpen(true);
    } catch (err) {
      console.error(err);
      alert("Failed to load profile");
    }
  };

  // fetch summary
  useEffect(() => {
    if (!programme) return;
    axios
      .get(`${API}/api/staff/students/count`, { params: { programme } })
      .then((res) => setTotalStudents(res.data.count));
    axios
      .get(`${API}/api/staff/students/countByLevel`, { params: { programme } })
      .then((res) => setByLevel(res.data));
  }, [API, programme]);

  // fetch students
  useEffect(() => {
    if (!programme) return;
    axios
      .get(`${API}/api/staff/students`, {
        params: {
          programme,
          level: studentLevel === "All" ? "" : studentLevel,
          search: studentSearch.trim(),
        },
      })
      .then((res) => setStudents(res.data));
    setStudentPage(1);
  }, [API, programme, studentLevel, studentSearch]);

  // fetch courses
  useEffect(() => {
    if (!programme) return;
    axios
      .get(`${API}/api/staff/courses`, {
        params: {
          programme,
          level: courseLevel === "All" ? "" : courseLevel,
          search: courseSearch.trim(),
        },
      })
      .then((res) => setCourses(res.data));
    setCoursePage(1);
  }, [API, programme, courseLevel, courseSearch]);

  // pagination
  const studentPages = Math.ceil(students.length / PAGE_SIZE);
  const coursePages = Math.ceil(courses.length / PAGE_SIZE);

  const pagedStudents = students.slice(
    (studentPage - 1) * PAGE_SIZE,
    studentPage * PAGE_SIZE,
  );
  const pagedCourses = courses.slice(
    (coursePage - 1) * PAGE_SIZE,
    coursePage * PAGE_SIZE,
  );

  return (
    <div className="staff-dashboard">
      <StaffSidebar />
      <main>
        <h1>Staff Dashboard</h1>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card total">
            <h2>Total Students</h2>
            <p className="big-number">{totalStudents}</p>
          </div>

          {CARD_LEVELS.map((lvl) => {
            const entry = byLevel.find((b) => b.Level === lvl) || { count: 0 };
            return (
              <div key={lvl} className="summary-card">
                <h4>{lvl}</h4>
                <p className="small-number">{entry.count}</p>
              </div>
            );
          })}
        </div>

        {/* Panels */}
        <div className="two-column modern">
          {/* Students Panel */}
          <section className="panel">
            <h3>Students</h3>
            <div className="controls">
              <input
                placeholder="Search…"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
              />
              <select
                value={studentLevel}
                onChange={(e) => setStudentLevel(e.target.value)}
              >
                {LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </select>
            </div>

            <ul className="list modern">
              {pagedStudents.map((s) => (
                <li key={s.id}>
                  <div>
                    <strong>{s.fullName}</strong>{" "}
                    <span>({s.matricNumber})</span>
                  </div>
                  <div>
                    <span className="lvl-tag">{s.Level}</span>
                    <button
                      className="btn-view"
                      onClick={() => handleViewProfile(s.id)}
                    >
                      View
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <Pagination
              page={studentPage}
              total={studentPages}
              onChange={setStudentPage}
            />
          </section>

          {/* Courses Panel */}
          <section className="panel">
            <h3>Courses</h3>
            <div className="controls">
              <input
                placeholder="Search…"
                value={courseSearch}
                onChange={(e) => setCourseSearch(e.target.value)}
              />
              <select
                value={courseLevel}
                onChange={(e) => setCourseLevel(e.target.value)}
              >
                {LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </select>
            </div>

            <table className="table modern">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Title</th>
                  <th>Level</th>
                  <th>Units</th>
                </tr>
              </thead>
              <tbody>
                {pagedCourses.map((c) => (
                  <tr key={c.CourseID}>
                    <td>{c.CourseCode}</td>
                    <td>{c.CourseTitle}</td>
                    <td>{c.Level}</td>
                    <td>{c.CreditUnits ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              page={coursePage}
              total={coursePages}
              onChange={setCoursePage}
            />
          </section>
        </div>

        {/* Profile Modal */}
        <Modal
          isOpen={modalOpen}
          onRequestClose={() => setModalOpen(false)}
          className="modal modern"
          overlayClassName="overlay"
        >
          <button className="modal-close" onClick={() => setModalOpen(false)}>
            ×
          </button>
          {profile ? (
            <div className="profile">
              <h2>{profile.fullName}</h2>
              <p>
                <strong>Matric No:</strong> {profile.matricNumber}
              </p>
              <p>
                <strong>Level:</strong> {profile.Level}
              </p>
              <p>
                <strong>Department:</strong> {profile.department}
              </p>
              <p>
                <strong>Email:</strong> {profile.email}
              </p>
              <p>
                <strong>Phone:</strong> {profile.phoneNumber}
              </p>
            </div>
          ) : (
            <p>Loading…</p>
          )}
        </Modal>
      </main>
    </div>
  );
}

function Pagination({ page, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div className="pagination modern">
      <button disabled={page === 1} onClick={() => onChange(page - 1)}>
        ‹
      </button>
      {[...Array(total)].map((_, i) => (
        <button
          key={i}
          className={i + 1 === page ? "active" : ""}
          onClick={() => onChange(i + 1)}
        >
          {i + 1}
        </button>
      ))}
      <button disabled={page === total} onClick={() => onChange(page + 1)}>
        ›
      </button>
    </div>
  );
}
